import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { SiteShell } from "@/components/layout/SiteShell";
import { AdSlot } from "@/components/AdSlot";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  todayISO,
  parseISO,
  addDays,
  diffDays,
  formatLong,
} from "@/lib/calendar/dates";
import { workdaysBetween, listWorkdaysBetween, type DayEntry } from "@/lib/calendar/info";
import { TERM_ENTITIES } from "@/lib/seo/entities";
import { MONTH_NAMES_ID } from "@/lib/calendar/dates";

const calcSearchSchema = z.object({
  // No .default() — that triggers TanStack to redirect bare URLs to a
  // query-string variant (307). fallback() alone supplies the value.
  mode: fallback(z.enum(["selisih", "tambah", "kurang", "kerja", "umur"]), "selisih"),
  from: fallback(z.string(), ""),
  to: fallback(z.string(), ""),
  base: fallback(z.string(), ""),
  n: fallback(z.coerce.number().int(), 7),
  birth: fallback(z.string(), "2000-01-01"),
  ref: fallback(z.string(), ""),
});

export const Route = createFileRoute("/tools/kalkulator-hari")({
  validateSearch: zodValidator(calcSearchSchema),
  head: () => {
    const url = "https://hari.co.id/tools/kalkulator-hari/";
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Kalkulator Hari Indonesia",
        applicationCategory: "UtilityApplication",
        operatingSystem: "Any",
        url,
        inLanguage: "id-ID",
        description:
          "Kalkulator hari: hitung selisih dua tanggal, tambah/kurang hari, hari kerja (exclude libur nasional), dan umur.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "IDR" },
        about: [TERM_ENTITIES.weton].filter(Boolean),
        potentialAction: [
          {
            "@type": "SearchAction",
            name: "Selisih dua tanggal",
            target: { "@type": "EntryPoint", urlTemplate: `${url}?mode=selisih&from={start_date}&to={end_date}` },
            "query-input": "required name=start_date required name=end_date",
          },
          {
            "@type": "SearchAction",
            name: "Hitung umur",
            target: { "@type": "EntryPoint", urlTemplate: `${url}?mode=umur&birth={birth_date}&ref={reference_date}` },
            "query-input": "required name=birth_date required name=reference_date",
          },
          {
            "@type": "SearchAction",
            name: "Tambah hari",
            target: { "@type": "EntryPoint", urlTemplate: `${url}?mode=tambah&base={base_date}&n={day_count}` },
            "query-input": "required name=base_date required name=day_count",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Beranda", item: "https://hari.co.id/" },
          { "@type": "ListItem", position: 2, name: "Kalkulator", item: "https://hari.co.id/tools/" },
          { "@type": "ListItem", position: 3, name: "Kalkulator Hari", item: url },
        ],
      },
    ];
    return {
      meta: [
        { title: "Kalkulator Hari: Selisih, Tambah/Kurang, Hari Kerja, Umur" },
        {
          name: "description",
          content:
            "Kalkulator hari online: hitung selisih dua tanggal, tambah/kurang hari, hari kerja (exclude libur nasional), umur dengan breakdown tahun, bulan, hari.",
        },
        { property: "og:title", content: "Kalkulator Hari Indonesia" },
        {
          property: "og:description",
          content:
            "Hitung selisih, tambah/kurang, hari kerja, dan umur secara akurat dengan data libur Indonesia.",
        },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: ld.map((obj) => ({ type: "application/ld+json", children: JSON.stringify(obj) })),
    };
  },
  component: KalkulatorPage,
});

function KalkulatorPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/tools/kalkulator-hari" });
  const set = (patch: Record<string, string | number>) =>
    navigate({ search: (prev: typeof search) => ({ ...prev, ...patch }) });
  return (
    <SiteShell>
      <header className="py-6">
        <h1 className="text-3xl font-bold tracking-tight">Kalkulator Hari</h1>
        <p className="mt-1 text-muted-foreground">
          Alat hitung tanggal: selisih, tambah/kurang hari, hari kerja, dan umur.
        </p>
      </header>
      <Tabs value={search.mode} onValueChange={(v) => set({ mode: v as typeof search.mode })} className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
          <TabsTrigger value="selisih">Selisih</TabsTrigger>
          <TabsTrigger value="tambah">Tambah</TabsTrigger>
          <TabsTrigger value="kurang">Kurang</TabsTrigger>
          <TabsTrigger value="kerja">Hari Kerja</TabsTrigger>
          <TabsTrigger value="umur">Umur</TabsTrigger>
        </TabsList>
        <TabsContent value="selisih"><SelisihTab from={search.from || todayISO()} to={search.to || todayISO()} set={set} /></TabsContent>
        <TabsContent value="tambah"><AddSubTab mode="add" base={search.base || todayISO()} n={search.n} set={set} /></TabsContent>
        <TabsContent value="kurang"><AddSubTab mode="sub" base={search.base || todayISO()} n={search.n} set={set} /></TabsContent>
        <TabsContent value="kerja"><KerjaTab from={search.from || todayISO()} to={search.to || todayISO()} set={set} /></TabsContent>
        <TabsContent value="umur"><UmurTab birth={search.birth} ref_={search.ref || todayISO()} set={set} /></TabsContent>
      </Tabs>
      <div className="mt-6">
        <AdSlot size="rectangle" position="tools" />
      </div>
      <ShareBar />
    </SiteShell>
  );
}

function ShareBar() {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3 text-sm">
      <span className="text-muted-foreground">Bagikan hasil ini:</span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          if (typeof window === "undefined") return;
          const url = window.location.href;
          if (navigator.share) {
            navigator.share({ title: "Kalkulator Hari", url }).catch(() => {});
          } else {
            navigator.clipboard.writeText(url).then(() => toast.success("Link disalin"));
          }
        }}
      >
        <Share2 className="mr-2 h-3.5 w-3.5" /> Salin link
      </Button>
      <Button variant="outline" size="sm" asChild>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`https://wa.me/?text=${encodeURIComponent(typeof window !== "undefined" ? `Hasil Kalkulator Hari: ${window.location.href}` : "")}`}
        >
          <MessageCircle className="mr-2 h-3.5 w-3.5" /> WhatsApp
        </a>
      </Button>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 rounded-lg border border-border bg-card p-4 sm:p-6">
      {children}
    </div>
  );
}

type Setter = (patch: Record<string, string | number>) => void;

function SelisihTab({ from, to, set }: { from: string; to: string; set: Setter }) {
  const result = useMemo(() => {
    if (!parseISO(from) || !parseISO(to)) return null;
    return diffDays(from, to);
  }, [from, to]);
  return (
    <Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="from">Tanggal awal</Label>
          <Input id="from" type="date" value={from} onChange={(e) => set({ from: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="to">Tanggal akhir</Label>
          <Input id="to" type="date" value={to} onChange={(e) => set({ to: e.target.value })} />
        </div>
      </div>
      {result !== null && (
        <p className="mt-4 text-lg">Selisih: <strong>{result} hari</strong></p>
      )}
    </Card>
  );
}

function AddSubTab({ mode, base, n, set }: { mode: "add" | "sub"; base: string; n: number; set: Setter }) {
  const result = useMemo(() => {
    if (!parseISO(base)) return null;
    const iso = addDays(base, mode === "add" ? n : -n);
    const p = parseISO(iso)!;
    return { iso, label: formatLong(p.y, p.m, p.d) };
  }, [base, n, mode]);
  return (
    <Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="base">Tanggal dasar</Label>
          <Input id="base" type="date" value={base} onChange={(e) => set({ base: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="n">Jumlah hari</Label>
          <Input id="n" type="number" min={0} value={n} onChange={(e) => set({ n: parseInt(e.target.value || "0", 10) })} />
        </div>
      </div>
      {result && <p className="mt-4 text-lg">Hasil: <strong>{result.label}</strong></p>}
    </Card>
  );
}

function KerjaTab({ from, to, set }: { from: string; to: string; set: Setter }) {
  const result = useMemo(() => {
    if (!parseISO(from) || !parseISO(to)) return null;
    return workdaysBetween(from, to);
  }, [from, to]);
  return (
    <Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="from2">Mulai</Label>
          <Input id="from2" type="date" value={from} onChange={(e) => set({ from: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="to2">Sampai</Label>
          <Input id="to2" type="date" value={to} onChange={(e) => set({ to: e.target.value })} />
        </div>
      </div>
      {result && (
        <>
          <div className="mt-4 grid gap-2 text-sm sm:grid-cols-4">
            <Stat label="Total hari" value={result.totalDays} />
            <Stat label="Hari kerja" value={result.workdays} />
            <Stat label="Akhir pekan" value={result.weekends} />
            <Stat label="Libur nasional" value={result.liburNasional} />
          </div>
          <DayListDisclosure from={from} to={to} />
        </>
      )}
    </Card>
  );
}

function UmurTab({ birth, ref_, set }: { birth: string; ref_: string; set: Setter }) {
  const result = useMemo(() => {
    const b = parseISO(birth);
    const r = parseISO(ref_);
    if (!b || !r) return null;
    let years = r.y - b.y;
    let months = r.m - b.m;
    let days = r.d - b.d;
    if (days < 0) {
      months -= 1;
      const prev = new Date(Date.UTC(r.y, r.m - 1, 0));
      days += prev.getUTCDate();
    }
    if (months < 0) { years -= 1; months += 12; }
    const totalDays = diffDays(birth, ref_);
    const wd = totalDays >= 0 && totalDays <= 60000 ? workdaysBetween(birth, ref_) : null;
    const totalMonths = years * 12 + months;
    const totalWeeks = Math.floor(totalDays / 7);
    return { years, months, days, totalDays, totalMonths, totalWeeks, wd };
  }, [birth, ref_]);
  return (
    <Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="birth">Tanggal lahir</Label>
          <Input id="birth" type="date" value={birth} onChange={(e) => set({ birth: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="ref">Hitung sampai</Label>
          <Input id="ref" type="date" value={ref_} onChange={(e) => set({ ref: e.target.value })} />
        </div>
      </div>
      {result && result.totalDays >= 0 && (
        <div className="mt-4 space-y-3">
          <p className="text-2xl font-bold">
            {result.years} tahun, {result.months} bulan, {result.days} hari
          </p>
          <p className="text-sm text-muted-foreground">
            Setara dengan {result.totalMonths.toLocaleString("id-ID")} bulan atau {result.totalWeeks.toLocaleString("id-ID")} minggu.
          </p>
          <div className="grid gap-2 text-sm sm:grid-cols-4">
            <Stat label="Total hari" value={result.totalDays} />
            {result.wd && <Stat label="Hari kerja" value={result.wd.workdays} />}
            {result.wd && <Stat label="Akhir pekan" value={result.wd.weekends} />}
            {result.wd && <Stat label="Libur nasional" value={result.wd.liburNasional} />}
          </div>
          {result.wd && (
            <p className="text-xs text-muted-foreground">
              Dari {result.totalDays.toLocaleString("id-ID")} hari, {result.wd.workdays.toLocaleString("id-ID")} adalah hari kerja (Senin-Jumat di luar libur nasional).
            </p>
          )}
          {result.totalDays > 0 && result.totalDays <= 60000 && (
            <DayListDisclosure from={birth} to={ref_} />
          )}
        </div>
      )}
      {result && result.totalDays < 0 && (
        <p className="mt-4 text-sm text-amber-600">Tanggal lahir berada setelah tanggal referensi.</p>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{typeof value === "number" ? value.toLocaleString("id-ID") : value}</p>
    </div>
  );
}

function DayListDisclosure({ from, to }: { from: string; to: string }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "kerja" | "akhir-pekan" | "libur">("all");
  const data = useMemo(() => listWorkdaysBetween(from, to, 5000), [from, to]);
  if (data.total <= 0) return null;
  const filtered = filter === "all" ? data.entries : data.entries.filter((e) => e.type === filter);
  // Group by year-month
  const groups: Record<string, DayEntry[]> = {};
  for (const e of filtered) {
    const k = e.iso.slice(0, 7);
    (groups[k] ||= []).push(e);
  }
  const monthKeys = Object.keys(groups).sort();
  return (
    <div className="mt-4 rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between p-3 text-left text-sm font-medium hover:bg-accent"
      >
        <span>{open ? "Sembunyikan" : "Tampilkan"} daftar tanggal ({data.total.toLocaleString("id-ID")} hari)</span>
        <span className="text-xs text-muted-foreground">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="border-t border-border p-3">
          <div className="mb-3 flex flex-wrap gap-1 text-xs">
            {(["all", "kerja", "akhir-pekan", "libur"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={
                  "rounded-full border px-3 py-1 " +
                  (filter === f ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent")
                }
              >
                {f === "all" ? "Semua" : f === "kerja" ? "Hari Kerja" : f === "akhir-pekan" ? "Akhir Pekan" : "Libur Nasional"}
              </button>
            ))}
          </div>
          {data.truncated && (
            <p className="mb-2 text-xs text-amber-600">Daftar dipotong di 5000 entri pertama.</p>
          )}
          <div className="max-h-80 overflow-y-auto pr-1">
            {monthKeys.map((k) => {
              const [y, m] = k.split("-").map(Number);
              return (
                <div key={k} className="mb-3">
                  <p className="sticky top-0 bg-card py-1 text-xs font-semibold text-muted-foreground">
                    {MONTH_NAMES_ID[m - 1]} {y}
                  </p>
                  <ul className="space-y-1 text-xs">
                    {groups[k].map((e) => {
                      const dd = parseInt(e.iso.slice(8, 10), 10);
                      return (
                        <li key={e.iso} className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-accent">
                          <span>
                            <span className="text-muted-foreground">{e.dayName.slice(0, 3)},</span>{" "}
                            <strong>{dd}</strong>
                            {e.liburName && <span className="ml-2 text-muted-foreground">{e.liburName}</span>}
                          </span>
                          <span
                            className={
                              "rounded px-2 py-0.5 text-[10px] font-medium " +
                              (e.type === "kerja"
                                ? "bg-emerald-500/15 text-emerald-700"
                                : e.type === "akhir-pekan"
                                  ? "bg-slate-500/15 text-slate-600"
                                  : "bg-rose-500/15 text-rose-700")
                            }
                          >
                            {e.type === "kerja" ? "Kerja" : e.type === "akhir-pekan" ? "Akhir pekan" : "Libur"}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground">Tidak ada hari yang cocok dengan filter.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}